import React, { useState, useEffect } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  VStack,
  HStack,
  Text,
  Badge,
  IconButton,
  Box,
  Divider,
  Button,
  useColorModeValue,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Flex,
  Select,
} from '@chakra-ui/react';
import { FiBell, FiCheck, FiTrash2, FiSettings, FiX } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getNotifications,
  markNotificationAsRead,
  deleteNotification,
  markAllNotificationsAsRead,
} from '../services/api';

const NotificationPopover = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('all');
  const { user: currentUser } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const unreadBgColor = useColorModeValue('blue.50', 'blue.900');
  const hoverBgColor = useColorModeValue('gray.100', 'gray.700');
  const iconColor = useColorModeValue('gray.600', 'gray.300');
  const hoverIconColor = useColorModeValue('purple.600', 'purple.300');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 15000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      const data = await getNotifications(currentUser?.role === 'admin' || currentUser?.role === 'superadmin' ? null : currentUser?._id);
      setNotifications(Array.isArray(data) ? data : []);
      setUnreadCount(Array.isArray(data) ? data.filter(n => !n.read).length : 0);
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast({
        title: 'Error',
        description: 'Failed to load notifications',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleMarkAsRead = async (id) => {
    if (!id) return;
    try {
      await markNotificationAsRead(id);
      setNotifications(prev => prev.map(n => 
        n._id === id ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark notification as read',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    try {
      await deleteNotification(id);
      setNotifications(prev => prev.filter(n => n._id !== id));
      setUnreadCount(prev => {
        const deletedNotification = notifications.find(n => n._id === id);
        return deletedNotification?.read ? prev : Math.max(0, prev - 1);
      });
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete notification',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast({
        title: 'Success',
        description: 'All notifications marked as read',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark all notifications as read',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleViewDetails = (notification) => {
    if (!notification.read) {
      handleMarkAsRead(notification._id);
    }
    navigate('/notifications', { state: { notification } });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'plan_renewal':
        return '💰';
      case 'system_update':
        return '🔄';
      case 'usage_alert':
        return '⚠️';
      case 'admin_notification':
        return '👨‍💼';
      case 'service_request':
        return '🔔';
      default:
        return '📢';
    }
  };

  const getNotificationColor = (priority) => {
    switch (priority) {
      case 'high':
      case 'urgent':
        return 'red';
      case 'medium':
        return 'orange';
      case 'low':
        return 'blue';
      default:
        return 'gray';
    }
  };

  // Ensure notifications is an array before filtering
  const filteredNotifications = Array.isArray(notifications) 
    ? notifications.filter(n => filter === 'all' || n.type === filter)
    : [];

  return (
    <Popover placement="bottom-end">
      <PopoverTrigger>
        <Box position="relative" display="inline-block">
          <IconButton
            icon={<FiBell />}
            variant="ghost"
            aria-label="Notifications"
            fontSize="xl"
            color={iconColor}
            _hover={{
              color: hoverIconColor
            }}
          />
          {unreadCount > 0 && (
            <Badge
              position="absolute"
              top="-1"
              right="-1"
              colorScheme="red"
              borderRadius="full"
              fontSize="xs"
              minW="20px"
              h="20px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {unreadCount}
            </Badge>
          )}
        </Box>
      </PopoverTrigger>
      <PopoverContent
        width="400px"
        bg={bgColor}
        borderColor={borderColor}
        boxShadow="lg"
        _focus={{ outline: 'none' }}
      >
        <PopoverArrow bg={bgColor} />
        <PopoverBody p={0}>
          <VStack spacing={0} align="stretch">
            <Flex
              justify="space-between"
              align="center"
              p={3}
              borderBottom="1px"
              borderColor={borderColor}
            >
              <Text fontWeight="bold">Notifications</Text>
              <HStack spacing={2}>
                <Select
                  size="sm"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  width="120px"
                >
                  <option value="all">All</option>
                  <option value="plan_renewal">Plan</option>
                  <option value="system_update">System</option>
                  <option value="usage_alert">Alerts</option>
                  <option value="admin_notification">Admin</option>
                  <option value="service_request">Service</option>
                </Select>
                {unreadCount > 0 && (
                  <Button
                    size="sm"
                    leftIcon={<FiCheck />}
                    onClick={handleMarkAllAsRead}
                    variant="ghost"
                  >
                    Mark all read
                  </Button>
                )}
              </HStack>
            </Flex>

            <Box maxH="400px" overflowY="auto">
              {filteredNotifications.length === 0 ? (
                <Box p={4} textAlign="center">
                  <Text color={textColor}>No notifications</Text>
                </Box>
              ) : (
                filteredNotifications.map((notification) => (
                  <Box
                    key={notification._id}
                    p={3}
                    bg={notification.read ? 'transparent' : unreadBgColor}
                    _hover={{ bg: hoverBgColor }}
                    cursor="pointer"
                    onClick={() => handleViewDetails(notification)}
                  >
                    <HStack spacing={3} align="start">
                      <Text fontSize="xl">{getNotificationIcon(notification.type)}</Text>
                      <VStack align="start" spacing={1} flex={1}>
                        <Text fontWeight={notification.read ? 'normal' : 'bold'}>
                          {notification.title}
                        </Text>
                        <Text fontSize="sm" color={textColor}>
                          {notification.message}
                        </Text>
                        <HStack spacing={2}>
                          <Badge
                            colorScheme={getNotificationColor(notification.priority)}
                            fontSize="xs"
                          >
                            {notification.priority}
                          </Badge>
                          <Text fontSize="xs" color={textColor}>
                            {new Date(notification.createdAt).toLocaleString()}
                          </Text>
                        </HStack>
                      </VStack>
                      <HStack spacing={1}>
                        {!notification.read && (
                          <IconButton
                            icon={<FiCheck />}
                            size="sm"
                            variant="ghost"
                            aria-label="Mark as read"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead(notification._id);
                            }}
                          />
                        )}
                        <IconButton
                          icon={<FiTrash2 />}
                          size="sm"
                          variant="ghost"
                          aria-label="Delete notification"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(notification._id);
                          }}
                        />
                      </HStack>
                    </HStack>
                  </Box>
                ))
              )}
            </Box>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationPopover; 